import { useState } from "react";
import { observer } from "mobx-react-lite";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";

import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import type { DocumentMenuConfig } from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { SignDocumentsDialog } from "@/domains/documents/components/organisms/SignDocumentsDialog";
import { SignaturePanelProvider } from "@/domains/documents/components/organisms/SignaturePanelProvider";
import type { SignableContainer } from "@/domains/documents/signing/signaturePanelContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { SIGNING_LIST_LIMIT } from "../repository";
import { useSigningAccess } from "../useSigningAccess";
import type { SigningPageState } from "../useSigningPageState";
import { SigningPreviewProvider } from "../SigningPreviewProvider";
import { useSigningPreview } from "../signingPreviewContext";
import type { SigningListItem } from "../types";
import {
  useSigningDocumentActions,
  type SigningDocumentActions,
} from "../useSigningDocumentActions";
import { SigningBulkActions } from "./SigningBulkActions";
import { SigningDetailSheet } from "./SigningDetailSheet";
import { SigningEmptyState } from "./SigningEmptyState";
import { SigningTable } from "./SigningTable";
import { SignTurnDialog } from "./SignTurnDialog";

interface SigningContentProps {
  buildingId: number;
  isLoading: boolean;
  error: unknown;
  /** A list query filled its cap — older rows exist but aren't loaded. */
  isTruncated: boolean;
  state: SigningPageState;
}

/** The list + detail panel, with the loading, error and empty states. */
export const SigningContent = observer(function SigningContent({
  buildingId,
  isLoading,
  error,
  isTruncated,
  state,
}: SigningContentProps) {
  const { t } = useLanguage();
  // One instance for the bulk bar AND the rows' right-click menu, so the page
  // has exactly one share dialog, mounted below.
  const actions = useSigningDocumentActions(buildingId);
  /**
   * The open sign-turn dialog is anchored HERE rather than inside the panel
   * whose button opened it, because finishing a turn destroys that panel.
   * Completing the session drops it from `listSigningSessions` (which excludes
   * `completed`), so the selection falls back to another row and the SessionPanel
   * subtree unmounts — taking "Signed. The signed container is filed with your
   * documents." with it before anyone can read it. The list's own poll does the
   * same thing to a user sitting on "Finalising…", without them touching
   * anything. Above the selection, the dialog outlives both and closes when the
   * user closes it.
   */
  const [signSessionId, setSignSessionId] = useState<string | null>(null);

  // The signature panel's "Add signature", wired the way the Documents page
  // wires it: the sign dialog on that one container, gated on who may START a
  // signing.
  const signingEnabled = useSigningAccess();
  const queryClient = useQueryClient();
  const [signTarget, setSignTarget] = useState<SignableContainer | null>(null);

  return (
    // The Documents page's viewers: the signature panel a signed container
    // opens in (with its enclosed files and its Share / Add signature), and
    // the one document viewer it owns, which the rows open in as well.
    <SignaturePanelProvider
      buildingId={String(buildingId)}
      onShare={
        actions.shareDocuments
          ? (containerId) => actions.shareDocuments?.([containerId])
          : undefined
      }
      onSign={signingEnabled ? setSignTarget : undefined}
    >
      <SigningPreviewProvider buildingId={buildingId}>
        <div className="flex-1 overflow-y-auto p-7">
          {error ? <ErrorAlert message={t.signing.loadFailed} /> : null}
          {isTruncated ? (
            <p className="mb-4 text-xs text-muted-foreground">
              {t.signing.truncated.replace("{count}", String(SIGNING_LIST_LIMIT))}
            </p>
          ) : null}
          <SigningBody actions={actions} isLoading={isLoading} error={error} state={state} />
          {/* Outside SigningBody, which renders nothing of the list when a
              search hides every row: the sheet belongs to the selection, so it
              stays open over the empty state until the user closes it. */}
          <SigningDetailSheet
            item={state.selectedItem}
            onClose={() => state.selectItem(null)}
            onSignTurn={setSignSessionId}
          />
          <SigningBulkActions
            items={state.checkedItems}
            actions={actions}
            onClearSelection={state.clearChecked}
          />
          {actions.dialogs}
          {signSessionId !== null && (
            <SignTurnDialog
              open
              onOpenChange={(next) => {
                if (!next) setSignSessionId(null);
              }}
              sessionId={signSessionId}
            />
          )}
          {signTarget !== null && (
            <SignDocumentsDialog
              open
              onOpenChange={(next) => {
                if (!next) setSignTarget(null);
              }}
              buildingId={buildingId}
              documents={[
                {
                  id: Number(signTarget.id),
                  filename: signTarget.filename,
                  size: signTarget.size ?? 0,
                },
              ]}
              onSigned={() => void queryClient.invalidateQueries({ queryKey: ["signing"] })}
            />
          )}
          <ChatLauncherSpacer />
        </div>
      </SigningPreviewProvider>
    </SignaturePanelProvider>
  );
});

function SigningBody({
  actions,
  isLoading,
  error,
  state,
}: Omit<SigningContentProps, "isTruncated" | "buildingId"> & {
  actions: SigningDocumentActions;
}) {
  if (isLoading) return <LoadingRows />;
  if (state.visibleItems.length === 0) {
    // A load failure must not masquerade as "nothing signed yet" — the alert
    // above is the whole story. Partially loaded items still render below.
    if (error) return null;
    return <SigningEmptyState tab={state.tab} isFiltered={state.isFiltered} />;
  }
  return <SigningLayout actions={actions} state={state} />;
}

const SigningLayout = observer(function SigningLayout({
  actions,
  state,
}: {
  actions: SigningDocumentActions;
  state: SigningPageState;
}) {
  const preview = useSigningPreview();
  const { actionsFor } = actions;

  // A row inside a larger checked selection acts on the whole selection, and
  // that menu is the same for every such row — bind it once per render, not
  // once per row (a select-all would otherwise re-walk the selection N times).
  const selectionActions =
    state.checkedItems.length > 1
      ? actionsFor(state.checkedItems, { onDone: state.clearChecked })
      : null;

  /**
   * The Documents page's right-click menu, bound the way that page binds it:
   * a row that is part of a larger checked selection acts on the whole
   * selection (the bulk bar's actions), any other row on itself alone.
   */
  const rowMenu = (item: SigningListItem): DocumentMenuConfig | null => {
    // A session has no document yet — nothing to share, download or open.
    if (item.kind !== "container" || item.documentId == null) return null;
    const isMulti = selectionActions != null && state.checkedKeys.has(item.key);
    const actions = isMulti ? selectionActions : actionsFor([item], { single: true });
    const canOpen = preview?.canOpenItem(item) ?? false;
    const isContainer = preview?.isContainerItem(item) ?? false;
    const open = () => preview?.openItem(item);
    return {
      isMulti,
      selectedCount: actions.count,
      filename: item.title,
      isDwg: false,
      canCompare: false,
      onOpen: canOpen && !isContainer ? open : undefined,
      onViewSignatures: canOpen && isContainer ? open : undefined,
      onShare: actions.onShare,
      onDownload: actions.onDownload,
      onToggleVerified: actions.onToggleVerified,
      verifiedState: actions.verifiedState,
      onAskFranklin: actions.onAskFranklin,
    };
  };

  return (
    <SigningTable
      items={state.visibleItems}
      selectedKey={state.selectedItem?.key ?? null}
      onSelect={(item) => state.selectItem(item.key)}
      sort={state.sort}
      onSortChange={state.toggleSort}
      checkedKeys={state.checkedKeys}
      onCheckedChange={state.setChecked}
      onCheckAll={state.setAllChecked}
      rowMenu={rowMenu}
    />
  );
});

function ErrorAlert({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function LoadingRows() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
    </div>
  );
}
